import type {
  InspectionDocumentDownloadDto,
  InspectionDocumentDto,
} from '@shared/dto/inspection/inspection-document.dto';
import type { InspectionInactivityPeriodDto } from '@shared/dto/inspection/inspection-inactivity-period.dto';
import type { InspectionLotRef } from '@shared/dto/inspection/inspection-lot-ref.dto';
import type { InspectionRegistryDto } from '@shared/dto/inspection/inspection-registry.dto';
import type { InspectionSessionDto } from '@shared/dto/inspection/inspection-session.dto';
import type { InspectionTransportDocumentDto } from '@shared/dto/inspection/inspection-transport-document.dto';
import type { RecallCaseDetailDto } from '@shared/dto/recall/recall-case-response.dto';
import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { type IInspectionHttpService, InspectionHttpService } from './inspection-http.service';

export type { InspectionTransportDocumentDto } from '@shared/dto/inspection/inspection-transport-document.dto';

export interface IInspectionPortalService {
  setSessionToken(token: string | null): void;
  openSession(token: string): Promise<InspectionSessionDto>;
  getRegistry(module: string, from?: string, to?: string): Promise<InspectionRegistryDto>;
  getInactivityPeriods(module: string): Promise<InspectionInactivityPeriodDto[]>;
  listDocuments(): Promise<InspectionDocumentDto[]>;
  getDocumentDownloadUrl(id: string): Promise<InspectionDocumentDownloadDto>;
  getRegistryAttachmentDownloadUrl(
    module: string,
    recordId: string,
  ): Promise<InspectionDocumentDownloadDto>;
  resolveLot(qrUuid: string): Promise<InspectionLotRef>;
  getQrIconName(uuid: string): Promise<{ iconName: string }>;
  downloadDossier(from: string, to: string): Promise<Blob>;
  listTransportDocuments(from?: string, to?: string): Promise<InspectionTransportDocumentDto[]>;
  downloadTransportDocumentPdf(id: string): Promise<Blob>;
  listRecallCases(): Promise<RecallCaseDetailDto[]>;
}

@Injectable()
class InspectionPortalService implements IInspectionPortalService {
  constructor(
    @inject(InspectionHttpService)
    private readonly inspectionHttp: IInspectionHttpService,
  ) {}

  setSessionToken(token: string | null): void {
    this.inspectionHttp.setSessionToken(token);
  }

  async openSession(token: string): Promise<InspectionSessionDto> {
    return this.inspectionHttp.post<InspectionSessionDto>('inspection/session', { token });
  }

  async getRegistry(module: string, from?: string, to?: string): Promise<InspectionRegistryDto> {
    const qs = from && to ? `?from=${from}&to=${to}` : '';
    return this.inspectionHttp.get<InspectionRegistryDto>(`inspection/registries/${module}${qs}`);
  }

  async getInactivityPeriods(module: string): Promise<InspectionInactivityPeriodDto[]> {
    return this.inspectionHttp.get<InspectionInactivityPeriodDto[]>(
      `inspection/inactivity-periods/${module}`,
    );
  }

  async listDocuments(): Promise<InspectionDocumentDto[]> {
    return this.inspectionHttp.get<InspectionDocumentDto[]>('inspection/documents');
  }

  async getDocumentDownloadUrl(id: string): Promise<InspectionDocumentDownloadDto> {
    const response = await this.inspectionHttp.get<{ downloadUrl: string }>(
      `inspection/documents/${id}/download`,
    );
    return { url: response.downloadUrl };
  }

  async getRegistryAttachmentDownloadUrl(
    module: string,
    recordId: string,
  ): Promise<InspectionDocumentDownloadDto> {
    const response = await this.inspectionHttp.get<{ downloadUrl: string }>(
      `inspection/registries/${module}/${recordId}/attachment`,
    );
    return { url: response.downloadUrl };
  }

  async resolveLot(qrUuid: string): Promise<InspectionLotRef> {
    return this.inspectionHttp.get<InspectionLotRef>(`inspection/lots/${qrUuid}`);
  }

  async getQrIconName(uuid: string): Promise<{ iconName: string }> {
    return this.inspectionHttp.get<{ iconName: string }>(`inspection/qr-icon/${uuid}`);
  }

  async downloadDossier(from: string, to: string): Promise<Blob> {
    const response = await this.inspectionHttp.postBlob('inspection/audit-zip', {
      from,
      to,
    });
    return response.blob();
  }

  async listTransportDocuments(
    from?: string,
    to?: string,
  ): Promise<InspectionTransportDocumentDto[]> {
    const qs = from && to ? `?from=${from}&to=${to}` : '';
    return this.inspectionHttp.get<InspectionTransportDocumentDto[]>(
      `inspection/transport-documents${qs}`,
    );
  }

  async downloadTransportDocumentPdf(id: string): Promise<Blob> {
    const response = await this.inspectionHttp.getBlob(`inspection/transport-documents/${id}/pdf`);
    return response.blob();
  }

  async listRecallCases(): Promise<RecallCaseDetailDto[]> {
    return this.inspectionHttp.get<RecallCaseDetailDto[]>('inspection/recalls');
  }
}

export { InspectionPortalService };
